import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FileSignature, LoaderCircle } from "lucide-react";

import { Button } from "@/design-system/primitives";
import { ApiError } from "@/lib/api/client";
import {
  type CosignScope,
  type CosignStatusFilter,
  useClinicalTeam,
  useClinicianSession,
  useCosignChart,
  useCosignReviews,
  useCosignSummary,
  useDrawCosignSample,
  useReassignCosigner,
} from "@/lib/api/clinicianHooks";
import { useBootstrap } from "@/lib/api/hooks";
import type { CosignReview } from "@/types/clinician";

/**
 * Chart reviews (cosign). Each month a share of every supervised
 * prescriber's (NP/PA) approvals is sampled into their supervising
 * physician's queue. The physician reads the locked physician note and
 * cosigns. Cosigning never blocks or changes a prescription.
 */

function monthKey(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function recentMonths(count: number) {
  const now = new Date();
  return Array.from({ length: count }, (_, index) => {
    const date = new Date(now.getFullYear(), now.getMonth() - index, 1);
    return {
      value: monthKey(date),
      label: date.toLocaleDateString(undefined, {
        month: "long",
        year: "numeric",
      }),
    };
  });
}

const PHYSICIAN = /,\s*(MD|DO)$/;

export function CosignScreen() {
  const { reviewId } = useParams();
  const navigate = useNavigate();
  const { data: bootstrap } = useBootstrap();
  const supervisor =
    bootstrap?.admin_role === "admin" ||
    bootstrap?.admin_role === "clinical_supervisor";
  const session = useClinicianSession();
  const [scope, setScope] = useState<CosignScope>("mine");
  const [status, setStatus] = useState<CosignStatusFilter>("pending");
  const [month, setMonth] = useState("");
  const months = useMemo(() => recentMonths(13), []);
  const reviews = useCosignReviews(scope, status, month || undefined);
  const rows = reviews.data ?? [];
  const selected = rows.find((row) => row.id === reviewId) ?? null;

  // While a new filter loads the query still shows the previous filter's
  // rows; selecting one of those would leave the new list with no chart open.
  const stale = reviews.isPlaceholderData;
  useEffect(() => {
    if (!reviewId && !stale && rows[0]) {
      navigate(`/clinician-portal/cosign/${rows[0].id}`, { replace: true });
    }
  }, [navigate, reviewId, rows, stale]);

  const scopes: { value: CosignScope; label: string }[] = [
    { value: "mine", label: "Assigned to me" },
    { value: "prescribed", label: "My charts" },
    ...(supervisor ? [{ value: "all" as const, label: "All" }] : []),
  ];

  return (
    <div className="max-w-[92rem] mx-auto px-12 md:px-24 xl:px-40 py-24 md:py-32 flex flex-col gap-18">
      <header>
        <p className="type-label-sm text-ink-muted">CHART REVIEW</p>
        <h1 className="type-heading-xl mt-4">Cosign</h1>
        <p className="type-body-sm text-ink-muted mt-6 max-w-[62rem]">
          Each month, a share of every nurse practitioner&rsquo;s or PA&rsquo;s
          approvals from the previous month is sampled for their supervising
          physician to review. Read the locked physician note and cosign.
          Cosigning never blocks or changes the prescription.
        </p>
      </header>

      {supervisor && <SupervisorPanel months={months} />}

      <div className="flex flex-wrap items-end gap-12">
        <div role="tablist" className="flex gap-4">
          {scopes.map((option) => (
            <button
              key={option.value}
              role="tab"
              type="button"
              aria-selected={scope === option.value}
              onClick={() => {
                setScope(option.value);
                navigate("/clinician-portal/cosign");
              }}
              className={`px-10 py-6 type-label-sm border ${
                scope === option.value
                  ? "border-ink bg-ink text-page"
                  : "border-line text-ink-muted hover:border-ink"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
        <label className="flex flex-col gap-3 type-label-xs text-ink-muted">
          STATUS
          <select
            value={status}
            onChange={(event) => {
              setStatus(event.target.value as CosignStatusFilter);
              navigate("/clinician-portal/cosign");
            }}
            className="border border-line bg-raised px-8 py-6 type-body-sm text-ink"
          >
            <option value="pending">Waiting for cosign</option>
            <option value="cosigned">Cosigned</option>
            <option value="all">All</option>
          </select>
        </label>
        <label className="flex flex-col gap-3 type-label-xs text-ink-muted">
          SAMPLE MONTH
          <select
            value={month}
            onChange={(event) => {
              setMonth(event.target.value);
              navigate("/clinician-portal/cosign");
            }}
            className="border border-line bg-raised px-8 py-6 type-body-sm text-ink"
          >
            <option value="">All months</option>
            {months.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        {reviews.isFetching && (
          <LoaderCircle
            className="animate-spin text-ink-muted mb-6"
            size={14}
            aria-label="Loading"
          />
        )}
      </div>

      {reviews.isError ? (
        <p className="type-body-sm text-danger">
          Chart reviews could not be loaded.
        </p>
      ) : rows.length === 0 && !reviews.isLoading ? (
        <p className="border border-line-subtle bg-raised p-18 type-body-sm text-ink-muted">
          No charts here.
          {scope === "mine" && status === "pending" && " Nothing is waiting for your cosign."}
        </p>
      ) : (
        <div className="grid lg:grid-cols-[minmax(18rem,26rem)_minmax(0,1fr)] gap-14 items-start">
          <aside className="border border-line-subtle bg-raised divide-y divide-line-subtle">
            {rows.map((row) => (
              <button
                key={row.id}
                type="button"
                onClick={() => navigate(`/clinician-portal/cosign/${row.id}`)}
                className={`w-full text-left px-12 py-10 flex flex-col gap-3 ${
                  row.id === selected?.id ? "bg-ink text-page" : "hover:bg-sunken"
                }`}
              >
                <span className="flex items-center justify-between gap-8">
                  <span className="type-body-sm font-medium truncate">
                    {row.patient_name || "Patient"}
                  </span>
                  <span className="type-label-xs shrink-0">
                    {row.status === "cosigned" ? "Cosigned" : "Waiting"}
                  </span>
                </span>
                <span className="type-label-xs opacity-75 truncate">
                  {row.product || row.medication || "Prescription"} ·{" "}
                  {row.prescriber || "Prescriber"}
                </span>
                <span className="type-label-xs opacity-75">
                  Approved {new Date(row.approved_at).toLocaleDateString()}
                  {row.patient_state ? ` · ${row.patient_state}` : ""}
                </span>
              </button>
            ))}
          </aside>
          {selected ? (
            <CosignDetail
              review={selected}
              me={session.data?.clinician_id ?? ""}
              supervisor={supervisor}
            />
          ) : (
            <p className="type-body-sm text-ink-muted">Choose a chart.</p>
          )}
        </div>
      )}
    </div>
  );
}

function CosignDetail({
  review,
  me,
  supervisor,
}: {
  review: CosignReview;
  me: string;
  supervisor: boolean;
}) {
  const cosign = useCosignChart();
  const reassign = useReassignCosigner();
  const team = useClinicalTeam(supervisor);
  const [note, setNote] = useState("");
  const [cosigner, setCosigner] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const mine = Boolean(me) && review.cosigner_clinician_id === me;
  const physicians = (team.data?.clinicians ?? []).filter(
    (member) =>
      member.active &&
      member.provider_type === "prescriber" &&
      PHYSICIAN.test(member.prescriber_name ?? ""),
  );

  useEffect(() => {
    setNote("");
    setCosigner("");
    setMessage(null);
  }, [review.id]);

  const submit = async () => {
    setMessage(null);
    try {
      await cosign.mutateAsync({ reviewId: review.id, note: note.trim() });
      setMessage("Cosigned.");
    } catch (error) {
      setMessage(
        error instanceof ApiError && error.status === 403
          ? "Only the supervising physician this chart is assigned to can cosign it."
          : "Could not cosign. Nothing was changed.",
      );
    }
  };

  const moveCosigner = async () => {
    if (!cosigner) return;
    setMessage(null);
    try {
      await reassign.mutateAsync({
        reviewId: review.id,
        cosignerClinicianId: cosigner,
      });
      setMessage("Cosigner updated.");
    } catch {
      setMessage("Could not change the cosigner. Nothing was changed.");
    }
  };

  return (
    <article className="border border-line-subtle bg-raised p-18 flex flex-col gap-14">
      <div className="flex flex-wrap items-start justify-between gap-10">
        <div>
          <h2 className="type-heading-md">
            {review.patient_name || "Patient"}
          </h2>
          <p className="type-body-sm text-ink-muted mt-3">
            {review.product || review.medication || "Prescription"}
            {review.order_number ? ` · ${review.order_number}` : ""}
            {review.patient_state ? ` · ${review.patient_state}` : ""}
          </p>
        </div>
        <Link
          to={`/clinician-portal/patients/${review.patient_id}`}
          className="type-label-sm underline"
        >
          Open patient chart
        </Link>
      </div>

      <dl className="grid sm:grid-cols-2 gap-x-18 gap-y-8 type-body-sm">
        <div>
          <dt className="type-label-xs text-ink-muted">PRESCRIBED BY</dt>
          <dd>{review.prescriber || "—"}</dd>
        </div>
        <div>
          <dt className="type-label-xs text-ink-muted">APPROVED</dt>
          <dd>{new Date(review.approved_at).toLocaleString()}</dd>
        </div>
        <div>
          <dt className="type-label-xs text-ink-muted">COSIGNER</dt>
          <dd>{review.cosigner || "Not assigned"}</dd>
        </div>
        <div>
          <dt className="type-label-xs text-ink-muted">SAMPLE</dt>
          <dd>
            {new Date(`${review.sample_month}T12:00:00`).toLocaleDateString(
              undefined,
              { month: "long", year: "numeric" },
            )}{" "}
            · {review.sample_percent}% of approvals
          </dd>
        </div>
      </dl>

      {review.medication && (
        <section>
          <h3 className="type-label-xs text-ink-muted">MEDICATION</h3>
          <p className="type-body-sm mt-3">{review.medication}</p>
          {review.directions && (
            <p className="type-body-sm text-ink-secondary mt-2 whitespace-pre-wrap">
              {review.directions}
            </p>
          )}
        </section>
      )}

      <section>
        <h3 className="type-label-xs text-ink-muted">
          LOCKED PHYSICIAN NOTE
        </h3>
        <p className="type-body-sm mt-4 whitespace-pre-wrap border border-line-subtle bg-sunken p-12">
          {review.physician_note || "No note text was recorded with this approval."}
        </p>
      </section>

      {review.status === "cosigned" ? (
        <p className="inline-flex items-center gap-6 type-body-sm text-success">
          <FileSignature size={14} aria-hidden />
          Cosigned by {review.cosigned_by_name || "the supervising physician"}
          {review.cosigned_at
            ? ` · ${new Date(review.cosigned_at).toLocaleString()}`
            : ""}
          {review.cosign_note ? ` — “${review.cosign_note}”` : ""}
        </p>
      ) : mine ? (
        <section className="flex flex-col gap-8">
          <label className="flex flex-col gap-4 type-label-xs text-ink-muted">
            REVIEW NOTE (OPTIONAL)
            <textarea
              value={note}
              onChange={(event) => setNote(event.target.value)}
              rows={3}
              maxLength={2000}
              className="border border-line bg-page px-9 py-8 type-body-sm text-ink resize-y"
              placeholder="Anything to record with your review"
            />
          </label>
          <div>
            <Button type="button" onClick={submit} loading={cosign.isPending}>
              <FileSignature size={14} aria-hidden /> Cosign chart
            </Button>
          </div>
        </section>
      ) : (
        <p className="type-body-sm text-ink-muted">
          Waiting for {review.cosigner || "a supervising physician"} to cosign.
        </p>
      )}

      {supervisor && review.status === "pending" && (
        <section className="border-t border-line-subtle pt-12 flex flex-wrap items-end gap-8">
          <label className="flex flex-col gap-4 type-label-xs text-ink-muted">
            REASSIGN COSIGNER
            <select
              value={cosigner}
              onChange={(event) => setCosigner(event.target.value)}
              className="border border-line bg-page px-8 py-6 type-body-sm text-ink"
            >
              <option value="">Choose a physician</option>
              {physicians.map((member) => (
                <option key={member.clinician_id} value={member.clinician_id}>
                  {member.prescriber_name || member.display_name}
                </option>
              ))}
            </select>
          </label>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={moveCosigner}
            loading={reassign.isPending}
            disabled={!cosigner}
          >
            Reassign
          </Button>
        </section>
      )}

      {message && (
        <p
          role="status"
          className={
            message.startsWith("Could not") || message.startsWith("Only")
              ? "type-body-sm text-danger"
              : "type-body-sm text-success"
          }
        >
          {message}
        </p>
      )}
    </article>
  );
}

function SupervisorPanel({
  months,
}: {
  months: { value: string; label: string }[];
}) {
  // Default to last month: the newest month that can be sampled.
  const [month, setMonth] = useState(months[1]?.value ?? months[0].value);
  const summary = useCosignSummary(month, true);
  const draw = useDrawCosignSample();
  const [message, setMessage] = useState<string | null>(null);
  const current = month === months[0].value;

  const drawSample = async () => {
    setMessage(null);
    try {
      const result = await draw.mutateAsync({ month });
      const drawn = result.prescribers.filter((item) => !item.already_sampled);
      setMessage(
        drawn.length
          ? `Sampled ${drawn.reduce((sum, item) => sum + item.sampled, 0)} chart(s) for ${drawn.length} prescriber(s).`
          : "Every prescriber already has a sample for this month.",
      );
    } catch (error) {
      setMessage(
        error instanceof ApiError && error.status === 409
          ? "Only a finished month can be sampled."
          : "Could not draw the sample.",
      );
    }
  };

  return (
    <section className="border border-line-subtle bg-raised">
      <div className="px-14 py-10 bg-sunken border-b border-line-subtle flex flex-wrap items-end justify-between gap-10">
        <div>
          <h2 className="type-heading-sm">Monthly cosign coverage</h2>
          <p className="text-11 text-ink-muted mt-2">
            Approvals by each supervised prescriber in the month, how many were
            sampled, and how many their physician has cosigned.
          </p>
        </div>
        <div className="flex items-end gap-8">
          <select
            value={month}
            onChange={(event) => {
              setMonth(event.target.value);
              setMessage(null);
            }}
            className="border border-line bg-raised px-8 py-6 type-body-sm"
            aria-label="Month"
          >
            {months.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <Button
            type="button"
            size="sm"
            onClick={drawSample}
            loading={draw.isPending}
            disabled={current}
            title={current ? "The month must finish first" : undefined}
          >
            Draw sample
          </Button>
        </div>
      </div>
      {summary.isLoading ? (
        <p className="p-14 type-body-sm text-ink-muted">Loading…</p>
      ) : summary.isError ? (
        <p className="p-14 type-body-sm text-danger">
          Coverage could not be loaded.
        </p>
      ) : (summary.data ?? []).length === 0 ? (
        <p className="p-14 type-body-sm text-ink-muted">
          No supervised prescribers yet. A prescriber is supervised when their
          profile has an NP or PA credential.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full type-body-sm">
            <thead>
              <tr className="text-left type-label-xs text-ink-muted">
                <th className="px-14 py-8">Prescriber</th>
                <th className="px-14 py-8 text-right">Approvals</th>
                <th className="px-14 py-8 text-right">Target</th>
                <th className="px-14 py-8 text-right">Sampled</th>
                <th className="px-14 py-8 text-right">Cosigned</th>
                <th className="px-14 py-8 text-right">Waiting</th>
                <th className="px-14 py-8 text-right">% of approvals cosigned</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line-subtle">
              {(summary.data ?? []).map((row) => (
                <tr key={row.prescriber_clinician_id}>
                  <td className="px-14 py-8">{row.prescriber || "—"}</td>
                  <td className="px-14 py-8 text-right tabular-nums">
                    {row.approvals}
                  </td>
                  <td className="px-14 py-8 text-right tabular-nums">
                    {row.target_percent}%
                  </td>
                  <td className="px-14 py-8 text-right tabular-nums">
                    {row.sampled}
                  </td>
                  <td className="px-14 py-8 text-right tabular-nums">
                    {row.cosigned}
                  </td>
                  <td className="px-14 py-8 text-right tabular-nums">
                    {row.pending}
                  </td>
                  <td className="px-14 py-8 text-right tabular-nums">
                    {row.percent_cosigned == null
                      ? "—"
                      : `${row.percent_cosigned}%`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {message && (
        <p
          role="status"
          className={`px-14 pb-10 type-body-sm ${
            message.startsWith("Could not") || message.startsWith("Only")
              ? "text-danger"
              : "text-success"
          }`}
        >
          {message}
        </p>
      )}
    </section>
  );
}
